import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { paymentsApi } from '../services/api';
import { CreditCard, X, CheckCircle, AlertTriangle, Loader } from 'lucide-react';

// ---- Types ----

interface StripeCheckoutProps {
  invoice: {
    id: string;
    invoice_number: string;
    total_eur: number;
    total_paid?: number;
    client_name?: string | null;
  };
  onClose: () => void;
  onSuccess: () => void;
  showToast: (msg: string, type: 'success' | 'error' | 'info') => void;
  /** Optional: custom function to create a PaymentIntent (e.g. from portal). Falls back to paymentsApi.createIntent. */
  createIntentFn?: (invoiceId: string) => Promise<{ data: { client_secret: string; publishable_key: string } }>;
}

// ---- Inner Payment Form (inside Elements context) ----

function CheckoutForm({
  invoice,
  onClose,
  onSuccess,
  showToast,
}: StripeCheckoutProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = invoice.total_eur - (invoice.total_paid || 0);
  const fmt = (n: number) =>
    n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message || 'Zahlung fehlgeschlagen');
      setProcessing(false);
    } else if (
      result.paymentIntent &&
      result.paymentIntent.status === 'succeeded'
    ) {
      setSucceeded(true);
      setProcessing(false);
      showToast('Zahlung erfolgreich!', 'success');
      setTimeout(() => onSuccess(), 1500);
    } else {
      setProcessing(false);
    }
  };

  if (succeeded) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <CheckCircle size={48} color="var(--success)" />
        <h3 style={{ marginTop: 16 }}>Zahlung erfolgreich!</h3>
        <p className="text-muted">
          {fmt(remaining)} wurde für {invoice.invoice_number} bezahlt.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ padding: '0 24px 16px' }}>
        <div
          style={{
            background: 'var(--bg-secondary, #f8f9fa)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: '0.9rem',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="text-muted">Rechnung</span>
            <strong>{invoice.invoice_number}</strong>
          </div>
          {invoice.client_name && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span className="text-muted">Klient</span>
              <span>{invoice.client_name}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span className="text-muted">Zu zahlen</span>
            <strong style={{ color: 'var(--primary)', fontSize: '1.05rem' }}>{fmt(remaining)}</strong>
          </div>
        </div>

        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 14px',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 6,
              color: '#991b1b',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={processing}>
          Abbrechen
        </button>
        <button type="submit" className="btn btn-accent" disabled={!stripe || processing}>
          {processing ? (
            <>
              <Loader size={14} className="spin" /> Verarbeitung...
            </>
          ) : (
            <>
              <CreditCard size={14} /> {fmt(remaining)} bezahlen
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ---- Main Component ----

export default function StripeCheckout(props: StripeCheckoutProps) {
  const { invoice, onClose, showToast, createIntentFn } = props;
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const res = createIntentFn
          ? await createIntentFn(invoice.id)
          : await paymentsApi.createIntent(invoice.id);
        if (cancelled) return;

        const { client_secret, publishable_key } = res.data;
        setClientSecret(client_secret);
        setStripePromise(loadStripe(publishable_key));
      } catch (err: any) {
        if (cancelled) return;
        const msg = err.response?.data?.detail || 'Stripe konnte nicht initialisiert werden';
        setError(msg);
        showToast(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [invoice.id, showToast, createIntentFn]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div>
            <h2>Online bezahlen</h2>
            <span className="text-sm text-muted">Sichere Zahlung via Stripe</span>
          </div>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {loading && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <Loader size={32} className="spin" style={{ color: 'var(--primary)' }} />
            <p className="text-muted" style={{ marginTop: 12 }}>
              Zahlungsformular wird geladen...
            </p>
          </div>
        )}

        {error && (
          <div style={{ padding: '24px' }}>
            <div
              style={{
                padding: '16px',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: 8,
                color: '#991b1b',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <AlertTriangle size={20} />
              <div>
                <strong>Fehler</strong>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>{error}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Schließen
              </button>
            </div>
          </div>
        )}

        {!loading && !error && clientSecret && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#6366f1',
                  borderRadius: '8px',
                },
              },
              locale: 'de',
            }}
          >
            <CheckoutForm {...props} />
          </Elements>
        )}
      </div>
    </div>
  );
}
